import { PaletteCategory, TCommonData, TWidget } from '@libs/types';
import { TUseHookTheme } from '../types/hooks';
import { DEFAULT_STYLE, TCoreOptions } from '../types/options';
import { selectTheme, useAppSelector } from '@libs/store';
import { useSystemColorScheme } from '@libs/theme';

const drillPath = (tree: Record<string, unknown> | undefined, path: string[]): unknown => {
    if (!tree) return undefined;
    let node: unknown = tree;
    for (const key of path) {
        if (node == null || typeof node !== 'object') return undefined;
        node = (node as Record<string, unknown>)[key];
    }
    return node;
};

export const useThemeHook = <TO extends TCoreOptions>(widget: TWidget<TCommonData, TO>, defaultStyle: string = DEFAULT_STYLE): TUseHookTheme<TO> => {
    const systemMode = useSystemColorScheme();
    const theme = useAppSelector(selectTheme);
    const colors = theme?.colors ?? {};
    const styles = theme?.styles ?? {};
    const currentPalette = (systemMode === 'dark' ? theme?.palette?.dark : theme?.palette?.light) ?? {};
    const { options = {} as TO } = widget;

    const styleKey = options.style as string;
    const parents = (options.parentStyleNames as string[] | undefined) ?? [];
    const path = [...parents, styleKey];

    // Resolve a node in a theme tree with the same fallback chain used for both palette and styles:
    //   1. Full path [...parents, styleKey], progressively dropping the outermost ancestor.
    //   2. Parent slot only [...parents] (no styleKey), again dropping the outermost ancestor.
    // The "parent slot" fallback lets parents=[a,b] resolve to tree.a.b when no tree.a.b[styleKey] leaf exists.
    const resolveByPath = (tree: Record<string, unknown>): Record<string, unknown> | undefined => {
        let result: Record<string, unknown> | undefined;
        for (let start = 0; start <= parents.length && result === undefined; start++) {
            result = drillPath(tree, path.slice(start)) as Record<string, unknown> | undefined;
        }
        for (let start = 0; start < parents.length && result === undefined; start++) {
            result = drillPath(tree, parents.slice(start)) as Record<string, unknown> | undefined;
        }
        return result;
    };

    const widgetTheme = (resolveByPath(currentPalette) ?? currentPalette[defaultStyle]) as PaletteCategory | undefined;
    const parentStylesTheme = (resolveByPath(styles) ?? styles[defaultStyle]) as Record<string, string> | undefined;
    const stylesTheme = { ...widgetTheme, ...(parentStylesTheme as Record<string, string>) };

    return { colors, palette: { ...widgetTheme, ...stylesTheme, ...options } };
};
